/**
 * "Remember Me", ported onto this reconstruction's storage.
 *
 * The original calls Firebase's `setPersistence` with the checkbox's value
 * (`sz.$d(b)` in `s8`), which picks between:
 *
 * - **LOCAL** — the session outlives the browser being closed;
 * - **SESSION** — the session dies with the tab.
 *
 * There is no Firebase here and the signed-in customer lives in redux-persist's
 * `localStorage` blob, which is always LOCAL. Rather than leave the control
 * inert — it is rendered, checked by default, and a customer who clears it has
 * expressed something real — the SESSION half is reproduced against
 * `sessionStorage`, which has exactly the lifetime Firebase means by the word:
 *
 * - the choice itself is written to `localStorage`, so it survives the reload
 *   it has to govern;
 * - when the choice is SESSION a marker is written to `sessionStorage`, which
 *   the browser drops when the tab closes;
 * - on the next boot `AuthBootstrap` asks `shouldDropStoredSession()`, and a
 *   SESSION choice whose marker is gone signs the customer out before anything
 *   authenticated runs.
 *
 * This is a PORT of a recovered behaviour, not an invented feature: the
 * behaviour is the original's, only the storage differs, and the difference is
 * forced by the transport deviation that removed Firebase.
 */

const PERSISTENCE_KEY = "eposmatic-web-auth-persistence";
const SESSION_MARKER_KEY = "eposmatic-web-auth-session";

type Persistence = "LOCAL" | "SESSION";

const read = (storage: Storage | undefined, key: string): string | null => {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const write = (storage: Storage | undefined, key: string, value: string): void => {
  try {
    storage?.setItem(key, value);
  } catch {
    /* storage disabled — the session simply behaves as LOCAL */
  }
};

/** Record the checkbox. Called on submit, before the code is sent. */
export const rememberSession = (remember: boolean): void => {
  if (typeof window === "undefined") return;
  const mode: Persistence = remember ? "LOCAL" : "SESSION";
  write(window.localStorage, PERSISTENCE_KEY, mode);
  if (mode === "SESSION") write(window.sessionStorage, SESSION_MARKER_KEY, "1");
};

/**
 * True when a stored session was created with "Remember Me" cleared and the
 * browsing session that created it has since ended.
 */
export const shouldDropStoredSession = (): boolean => {
  if (typeof window === "undefined") return false;
  if (read(window.localStorage, PERSISTENCE_KEY) !== "SESSION") return false;
  return read(window.sessionStorage, SESSION_MARKER_KEY) == null;
};

/** Forget the choice. Called on sign-out, so the next login starts clean. */
export const clearSessionPersistence = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(PERSISTENCE_KEY);
    window.sessionStorage.removeItem(SESSION_MARKER_KEY);
  } catch {
    /* nothing to clear */
  }
};
